export type PaginateRowsInput = {
  /** Measured height of every data row, in px, in render order. */
  rowHeights: number[];
  /** Printable height of one sheet, in px. */
  contentHeight: number;
  /** Header block height, repeated on every sheet. */
  headerHeight: number;
  /** Column-heading row height, repeated on every sheet. */
  theadHeight: number;
  /** Footer block height; it only occupies the last sheet. */
  footerHeight: number;
  /** Rendered no-data notice, present when the table has no printable rows. */
  emptyNoticeHeight?: number;
  /** Space where page numbering would otherwise intrude into report content. */
  paginationReserveHeight?: number;
  /**
   * Height held back from every sheet. Guards against a fractional pixel
   * pushing the last row past the bottom edge; the sheets do not clip, so an
   * overflow would show as an overhang on paper.
   */
  safetyPx?: number;
};

export type PaginateRowsResult = {
  /** Row indexes per sheet. Always at least one sheet, even with no rows. */
  pages: number[][];
  /** The footer did not fit under the last row and takes a sheet of its own. */
  footerOnOwnPage: boolean;
};

/**
 * Splits data rows across sheets of paper.
 *
 * The renderer paginates instead of leaving it to the browser's page breaks: a
 * report has to be laid out as real sheets so what is on screen is what comes
 * out of the printer, the column headings repeat per sheet, and the footer
 * totals land on the last sheet only.
 *
 * A row taller than the whole printable area still gets a sheet rather than
 * looping forever — it will overflow that sheet, which is visible and fixable,
 * unlike a hang.
 */
export const paginateRows = ({
  rowHeights,
  contentHeight,
  headerHeight,
  theadHeight,
  footerHeight,
  emptyNoticeHeight = 0,
  paginationReserveHeight = 0,
  safetyPx = 2,
}: PaginateRowsInput): PaginateRowsResult => {
  const layout = (reserveHeight: number): PaginateRowsResult => {
    const available =
      contentHeight -
      headerHeight -
      theadHeight -
      safetyPx -
      reserveHeight;
    const pages: number[][] = [];
    let current: number[] = [];
    let used = 0;

    rowHeights.forEach((rowHeight, index) => {
      if (current.length > 0 && used + rowHeight > available) {
        pages.push(current);
        current = [];
        used = 0;
      }

      current.push(index);
      used += rowHeight;
    });

    pages.push(current);

    const lastPageHeight =
      pages[pages.length - 1].reduce(
        (total, index) => total + rowHeights[index],
        0
      ) + emptyNoticeHeight;
    const footerOnOwnPage =
      footerHeight > 0 && lastPageHeight + footerHeight > available;

    return { pages, footerOnOwnPage };
  };

  // A single sheet has no page number, so reserving its band could create an
  // unnecessary second sheet. Reflow only after the unreserved pass proves
  // that numbering will actually be rendered.
  const unreserved = layout(0);
  const sheetCount =
    unreserved.pages.length + (unreserved.footerOnOwnPage ? 1 : 0);
  return sheetCount > 1 && paginationReserveHeight > 0
    ? layout(paginationReserveHeight)
    : unreserved;
};
